import { useEffect, useState, useRef, useCallback, useMemo } from "react";
import HeaderTitle from "../../../components/HeaderTitle";
import { Table, Modal } from "antd";
import MyEmpty from "../../../components/Empty";
import ReactJson from "react-json-view";
import projectApi from "../../../apis/project";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";

// 错误码常量
const ERROR_DATA = [
  { key: 1, name: "-2", example: "间隔重试" },
  { key: 2, name: "-1", example: "间隔重试，多次失败后不再重试" },
  { key: 3, name: "0", example: "不可重试" },
  { key: 4, name: "1", example: "执行成功" },
  { key: 5, name: "2", example: "间隔轮询重试" },
];

// 通用转义处理：将后端返回的字符串中的 \\n、\\t、\\" 转为正常字符
const decodeEscapedString = (value) => {
  if (typeof value !== "string") return value;

  let result = value;
  // 处理转义的双引号
  if (result.includes('\\"')) {
    result = result.replace(/\\"/g, '"');
  }
  // 处理制表符为空格
  if (result.includes("\\t")) {
    result = result.replace(/\\t/g, "  ");
  }
  // 处理换行符为真实换行
  if (result.includes("\\n")) {
    result = result.replace(/\\n/g, "\n");
  }

  return result;
};

// 描述单元格组件，用于检测是否超过2行
const DescriptionCell = ({
  description,
  onDescriptionClick,
  highlightText,
}) => {
  const cellRef = useRef(null);
  const [isOverflow, setIsOverflow] = useState(false);

  useEffect(() => {
    const checkOverflow = () => {
      if (cellRef.current) {
        const isTextOverflow =
          cellRef.current.scrollHeight > cellRef.current.clientHeight;
        setIsOverflow(isTextOverflow);
      }
    };

    requestAnimationFrame(checkOverflow);
    const timer = setTimeout(checkOverflow, 100);

    return () => clearTimeout(timer);
  }, [description]);

  if (!description) return null;

  // 表格中的描述统一走转义解析
  const displayDescription = decodeEscapedString(description);

  const handleMouseEnter = (e) => {
    if (isOverflow) {
      e.currentTarget.style.color = "#006AB2";
    }
  };

  const handleMouseLeave = (e) => {
    if (isOverflow) {
      e.currentTarget.style.color = "";
    }
  };

  const handleClick = () => {
    if (isOverflow) {
      onDescriptionClick(displayDescription);
    }
  };

  return (
    <div
      ref={cellRef}
      className="description-cell"
      style={{
        display: "-webkit-box",
        WebkitLineClamp: 2,
        WebkitBoxOrient: "vertical",
        overflow: "hidden",
        textOverflow: "ellipsis",
        // 只有需要弹窗（超过两行）时才显示手型光标
        cursor: isOverflow ? "pointer" : undefined,
        wordBreak: "break-word",
      }}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onClick={handleClick}
    >
      {highlightText(displayDescription)}
    </div>
  );
};

// 示例值单元格组件，用于检测是否超过2行
const ExampleValueCell = ({ value, onValueClick, highlightText }) => {
  const cellRef = useRef(null);
  const [isOverflow, setIsOverflow] = useState(false);

  useEffect(() => {
    const checkOverflow = () => {
      if (cellRef.current) {
        const isTextOverflow =
          cellRef.current.scrollHeight > cellRef.current.clientHeight;
        setIsOverflow(isTextOverflow);
      }
    };

    requestAnimationFrame(checkOverflow);
    const timer = setTimeout(checkOverflow, 100);

    return () => clearTimeout(timer);
  }, [value]);

  if (!value) return null;

  // 表格中的示例值统一走转义解析
  const displayValue = decodeEscapedString(value);

  const handleMouseEnter = (e) => {
    if (isOverflow) {
      e.currentTarget.style.color = "#006AB2";
    }
  };

  const handleMouseLeave = (e) => {
    if (isOverflow) {
      e.currentTarget.style.color = "";
    }
  };

  const handleClick = () => {
    if (isOverflow) {
      onValueClick(displayValue);
    }
  };

  return (
    <div
      ref={cellRef}
      className="example-value-cell"
      style={{
        display: "-webkit-box",
        WebkitLineClamp: 2,
        WebkitBoxOrient: "vertical",
        overflow: "hidden",
        textOverflow: "ellipsis",
        // 只有需要弹窗（超过两行）时才显示手型光标
        cursor: isOverflow ? "pointer" : undefined,
        wordBreak: "break-word",
      }}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onClick={handleClick}
    >
      {highlightText(displayValue)}
    </div>
  );
};

const ApiContent = (props) => {
  const { id } = props;
  const selectName = useSelector((state) => state.Project.newselectName);
  const Navigate = useNavigate();
  const [requestData, setRequestData] = useState([]);
  const [requestBodyData, setRequestBodyData] = useState([]);
  const [respondData, setRespondData] = useState([]);
  const [requestBodyExample, setRequestBodyExample] = useState({});
  const [respondExample, setRespondExample] = useState({});
  const [defaultData, setdefaultData] = useState({});
  const [apiInfo, setApiInfo] = useState({});
  const [modalVisible, setModalVisible] = useState(false);
  const [modalContent, setModalContent] = useState("");
  const [modalTitle, setModalTitle] = useState("参数描述");

  // 转义正则特殊字符
  const escapeRegExp = useCallback((str) => {
    return str.replace(/[.*+?^=!:${}()|[\]/\\]/g, "\\$&");
  }, []);

  // 高亮文本
  const highlightText = useCallback(
    (text) => {
      if (!text) return text;

      // 所有参与高亮展示的文本，统一先做一次转义解析
      const safeText = decodeEscapedString(text);
      if (!selectName || !safeText) return safeText;

      const escapedName = escapeRegExp(selectName);
      const parts = String(safeText).split(
        new RegExp(`(${escapedName})`, "gi"),
      );

      return parts.map((part, idx) =>
        part.toLowerCase() === selectName.toLowerCase() ? (
          <span key={idx} style={{ color: "#fe545f", fontWeight: "bold" }}>
            {part}
          </span>
        ) : (
          part
        ),
      );
    },
    [selectName, escapeRegExp],
  );

  // 获取API详情
  const getApiDetail = useCallback(() => {
    if (!id) return;

    projectApi
      .getApiDetail(id)
      .then((res) => {
        if (res.code === 200) {
          try {
            const parsedData = JSON.parse(res.data.req_parm_exp);
            setdefaultData(parsedData);
            setApiInfo(res.data);
          } catch (error) {
            console.error("解析API数据失败:", error);
            setApiInfo({});
            setdefaultData({});
          }
        } else {
          setApiInfo({});
          setdefaultData({});
        }
      })
      .catch((error) => {
        console.error("获取API详情失败:", error);
        setApiInfo({});
        setdefaultData({});
      });
  }, [id]);

  useEffect(() => {
    getApiDetail();
  }, [getApiDetail]);

  // 处理请求体参数
  const extractChildren = useCallback((data) => {
    if (data.dataType === "Object" || data.dataType === "Array") {
      if (Array.isArray(data.children)) {
        const children = data.children
          .map((child) => extractChildren(child))
          .filter((child) => child !== null);
        return { ...data, children };
      }
      return null;
    }
    return data;
  }, []);

  const transformData = useCallback((data) => {
    return data?.map((obj, index) => {
      if (obj.children?.length === 0) {
        const { children, ...rest } = obj;
        return { ...rest, only: index };
      }
      return { ...obj, only: index };
    });
  }, []);

  const flattenArray = useCallback((arr) => {
    return arr.reduce((flat, item) => {
      return flat.concat(Array.isArray(item) ? flattenArray(item) : item);
    }, []);
  }, []);

  const cleanStructure = useCallback((obj) => {
    if (Array.isArray(obj)) {
      return obj.map(cleanStructure);
    }

    if (obj && typeof obj === "object") {
      const newObj = {};

      for (const key in obj) {
        if (obj.hasOwnProperty(key)) {
          newObj[key] = cleanStructure(obj[key]);
        }
      }

      const type = ["Object", "Array"];
      if (!type.includes(newObj.dataType)) {
        delete newObj.children;
      } else if (newObj.children) {
        newObj.children = cleanStructure(newObj.children);
      }

      return newObj;
    }

    return obj;
  }, []);

  // 处理响应数据
  const processResponseData = useCallback(
    (responseBodyDefinition) => {
      if (!responseBodyDefinition?.children) {
        return [];
      }

      const respond = responseBodyDefinition.children.find(
        (item) => item.dataType === "Object" || item.dataType === "Array",
      );
      const string = responseBodyDefinition.children.find(
        (item) =>
          item.name === "result" &&
          (item.dataType === "Integer" || item.dataType === "String"),
      );

      if (string) {
        const data = { ...string };
        delete data.children;
        return [data];
      }

      const findNonStringDataType = () => {
        return responseBodyDefinition.children.find(
          (child) => child.dataType !== "String",
        );
      };

      const nonStringItem = findNonStringDataType();
      if (!nonStringItem) {
        const stringData = responseBodyDefinition.children[2];
        if (stringData) {
          const news = transformData([stringData]);
          return cleanStructure(news);
        }
        return [];
      }

      if (respond?.dataType === "Object") {
        const integerChild =
          respond.children?.filter(
            (child) =>
              child?.dataType === "Array" || child?.dataType === "Object",
          ) || [];

        const otherChildren =
          respond.children?.filter((child) => child?.dataType !== "Array") ||
          [];

        let combinedArrayChildren = integerChild.flatMap((child) =>
          child.children?.[0]?.children?.length > 0
            ? child.children[0].children
            : [child],
        );

        let flattenedCombinedArrayChildren = flattenArray(
          combinedArrayChildren,
        );

        let respondData =
          integerChild.length !== 0
            ? [...otherChildren, ...flattenedCombinedArrayChildren]
            : respond.children || [];

        const uniqueData = respondData.reduce((acc, item) => {
          const existingItem = acc.find((e) => e.name === item.name);
          if (!existingItem) {
            acc.push(item);
          } else if (item.description && !existingItem.description) {
            Object.assign(existingItem, item);
          }
          return acc;
        }, []);

        const news = transformData(uniqueData);
        return cleanStructure(news);
      } else if (respond?.children?.[0]?.children) {
        const news = transformData(respond.children[0].children);
        return cleanStructure(news);
      }

      return [];
    },
    [transformData, cleanStructure, flattenArray],
  );

  useEffect(() => {
    if (Object.keys(defaultData).length === 0) return;

    // 请求头参数
    if (defaultData.parameters?.length > 0) {
      setRequestData(defaultData.parameters);
    }

    // 请求体参数
    if (defaultData.requestBody) {
      if (
        defaultData.requestBodyDefinition !== null &&
        defaultData.requestBodyDefinition !== undefined
      ) {
        const data = [defaultData.requestBodyDefinition]
          .map((data) => extractChildren(data))
          .filter((data) => data !== null);
        const cleanedData = cleanStructure(data);
        setRequestBodyData(cleanedData);
      }
    }

    // 响应参数
    if (defaultData.responseBodyDefinition) {
      const processedData = processResponseData(
        defaultData.responseBodyDefinition,
      );
      setRespondData(processedData);
    } else {
      setRespondData([]);
    }

    // 响应示例
    if (defaultData.responseBody) {
      try {
        setRespondExample(JSON.parse(defaultData.responseBody));
      } catch (error) {
        console.error("解析响应示例失败:", error);
        setRespondExample({});
      }
    }

    // 请求体示例
    if (defaultData.requestBody) {
      try {
        setRequestBodyExample(JSON.parse(defaultData.requestBody));
      } catch (error) {
        console.error("解析请求体示例失败:", error);
        setRequestBodyExample({});
      }
    }
  }, [defaultData, extractChildren, cleanStructure, processResponseData]);

  // 处理描述文本点击，打开弹窗
  const handleDescriptionClick = useCallback((description) => {
    if (description) {
      setModalTitle("参数描述");
      setModalContent(description);
      setModalVisible(true);
    }
  }, []);

  // 处理示例值点击，打开弹窗
  const handleExampleValueClick = useCallback((value) => {
    if (value) {
      setModalTitle("示例值");
      setModalContent(value);
      setModalVisible(true);
    }
  }, []);

  // 创建通用表格列配置
  const createCommonColumns = useCallback(
    (includeDescription = true) => {
      const columns = [
        {
          title: "参数名",
          dataIndex: "name",
          width: "15%",
          render: (_, info) => highlightText(info?.name),
        },
      ];

      if (includeDescription) {
        columns.push({
          title: "参数描述",
          dataIndex: "description",
          width: "15%",
          render: (_, info) => (
            <DescriptionCell
              description={info?.description}
              onDescriptionClick={handleDescriptionClick}
              highlightText={highlightText}
            />
          ),
        });
      } else {
        columns.push({
          title: "参数描述",
          dataIndex: "description",
          width: "15%",
          render: (_, info) => highlightText(info?.description),
        });
      }

      columns.push(
        {
          title: "参数类型",
          dataIndex: "dataType",
          width: includeDescription ? "8%" : "10%",
          render: (_, info) => highlightText(info?.dataType),
        },
        {
          title: "是否必填",
          dataIndex: "required",
          width: "8%",
          render: (_, info) => (info?.required ? "是" : "否"),
        },
        {
          title: "示例值",
          dataIndex: "value",
          width: "15%",
          render: (_, info) => highlightText(info?.value),
        },
      );

      return columns;
    },
    [highlightText, handleDescriptionClick],
  );

  const requestColumns = useMemo(
    () => createCommonColumns(false),
    [createCommonColumns],
  );

  const requestBodyColumns = useMemo(
    () => createCommonColumns(false),
    [createCommonColumns],
  );

  const respondColumns = useMemo(
    () => [
      {
        title: "参数名",
        dataIndex: "name",
        width: "15%",
        render: (_, info) => highlightText(info?.name),
      },
      {
        title: "参数描述",
        dataIndex: "description",
        width: "15%",
        render: (_, info) => (
          <DescriptionCell
            description={info?.description}
            onDescriptionClick={handleDescriptionClick}
            highlightText={highlightText}
          />
        ),
      },
      {
        title: "参数类型",
        dataIndex: "dataType",
        width: "10%",
        render: (_, info) => highlightText(info?.dataType),
      },
      {
        title: "示例值",
        dataIndex: "value",
        width: "30%",
        className: "wrap-column",
        render: (_, info) => (
          <ExampleValueCell
            value={info?.value}
            onValueClick={handleExampleValueClick}
            highlightText={highlightText}
          />
        ),
      },
    ],
    [highlightText, handleDescriptionClick, handleExampleValueClick],
  );

  const errorColumns = useMemo(
    () => [
      {
        title: "代码",
        dataIndex: "name",
      },
      {
        title: "说明",
        dataIndex: "example",
      },
    ],
    [],
  );

  const handleClick = useCallback(() => {
    const currentUrl = window.location.href;
    const urlParams = new URLSearchParams(new URL(currentUrl).search);
    const showName = urlParams.get("show_name");

    if (showName) {
      window.open("/overview", "_blank");
    } else {
      Navigate("/overview", { replace: true });
    }
  }, [Navigate]);

  return (
    <div className="apiReference">
      <div className="contentValue">
        <div className="BasicInfo">
          <HeaderTitle title="基本信息"></HeaderTitle>
          <ul className="basicItem">
            <li>
              <div></div>
              <span className="child">接口id：</span>
              <span> {apiInfo?.api_id}</span>
            </li>
            <li>
              <div></div>
              <span className="child">接口Url：</span>
              <span>
                {`https://api.touzhiwang.com/data_service${apiInfo?.api_url}`}
              </span>
            </li>
            <li>
              <div></div>
              <span className="child">请求方式：</span>
              <span> {apiInfo?.req_method_code}</span>
            </li>
            <li>
              <div></div>
              <span className="child"> Content-Type：</span>
              <span> {apiInfo?.content_type}</span>
            </li>
            <li>
              <div></div>
              <span className="child">接口描述：</span>
              <span style={{ whiteSpace: "pre-line" }}>
                {highlightText(defaultData.description)}
              </span>
            </li>
          </ul>
        </div>
        <div className="requestParam">
          <HeaderTitle title="请求参数"></HeaderTitle>
          <div className="headers">
            <div>
              <div>
                需要添加请求header
                <span
                  onClick={handleClick}
                  style={{
                    color: "#1890ff",
                    marginLeft: "10px",
                    cursor: "pointer",
                  }}
                >
                  没有申请请跳转
                  <span style={{ textDecoration: "underline" }}>申请服务</span>
                </span>
              </div>
              <div> 例: Authorization: Bearer ***</div>
            </div>
          </div>
          {defaultData?.parameters?.length > 0 && (
            <>
              <div className="requestName">params</div>
              <Table
                className="myFormTable"
                columns={requestColumns}
                dataSource={requestData}
                bordered
                pagination={false}
                locale={{
                  emptyText: <MyEmpty></MyEmpty>,
                }}
                rowKey={(record) =>
                  record.name || `param-${record.dataType || "unknown"}`
                }
              />
            </>
          )}
          {defaultData?.requestBody && (
            <>
              <div className="requestName" style={{ marginTop: "20px" }}>
                requestBody
              </div>
              <Table
                className="myFormTable"
                columns={requestBodyColumns}
                dataSource={requestBodyData}
                bordered
                pagination={false}
                locale={{
                  emptyText: <MyEmpty></MyEmpty>,
                }}
                rowKey={(record) => {
                  // 优先使用 name，其次使用 only，最后使用组合字段
                  if (record.name) return record.name;
                  if (record.only !== undefined) return `body-${record.only}`;
                  return `body-${record.dataType || "unknown"}-${record.value || ""}`;
                }}
              />
            </>
          )}
        </div>
        <div className="respondParam">
          <HeaderTitle title="返回参数"></HeaderTitle>
          <Table
            className="myFormTable"
            columns={respondColumns}
            dataSource={respondData}
            bordered
            pagination={false}
            locale={{
              emptyText: <MyEmpty></MyEmpty>,
            }}
            scroll={{ x: 500 }}
            rowKey={(record) => record.only ?? record.name}
          />
        </div>
        <div className="exampleInfo">
          <HeaderTitle title="示例"></HeaderTitle>
          {Object.keys(requestBodyExample).length > 0 && (
            <div className="exampleItem">
              <div className="example">请求体示例</div>
              <div className="value">
                <ReactJson
                  style={{
                    fontFamily: "sans-serif",
                  }}
                  name={null}
                  enableClipboard={true}
                  displayDataTypes={false}
                  displayObjectSize={false}
                  displayArrayKey={false}
                  displayArrayIndex={false}
                  src={requestBodyExample}
                  groupArraysAfterLength={10}
                  collapsed={1}
                ></ReactJson>
              </div>
            </div>
          )}
          <div className="exampleItem">
            <div className="example">返回示例</div>
            <div className="value">
              <ReactJson
                style={{
                  fontFamily: "sans-serif",
                }}
                name={null}
                enableClipboard={true}
                displayDataTypes={false}
                displayObjectSize={false}
                displayArrayKey={false}
                displayArrayIndex={false}
                src={respondExample}
                groupArraysAfterLength={10}
                collapsed={1}
              ></ReactJson>
            </div>
          </div>
        </div>
        <div className="errorCodeInfo">
          <HeaderTitle title="错误码"></HeaderTitle>
          <div className="errorValue">
            <Table
              className="myFormTable"
              columns={errorColumns}
              dataSource={ERROR_DATA}
              bordered
              pagination={false}
              locale={{
                emptyText: <MyEmpty></MyEmpty>,
              }}
              rowKey={(record) => record.key}
            />
          </div>
        </div>
      </div>
      <Modal
        title={modalTitle}
        visible={modalVisible}
        onCancel={() => setModalVisible(false)}
        footer={null}
        centered
        width={600}
      >
        <div
          style={{
            maxHeight: "600px",
            overflowY: "auto",
            whiteSpace: "pre-wrap",
            wordBreak: "break-word",
            padding: "10px",
            userSelect: "text",
            WebkitUserSelect: "text",
          }}
        >
          {highlightText(modalContent)}
        </div>
      </Modal>
    </div>
  );
};

export default ApiContent;
